/**
 * TransactionMatchingPanel.js
 * Advanced interface for matching bank transactions with journal entries
 */

import React, { useState, useMemo, useCallback } from 'react';
import { Box } from '@mui/material';
import { toastNotification } from '../../shared/toastNotification';
import { isReconciled } from '../../helpers/reconciliationHelpers';
import { removeReconciliation } from '../../services/api/reconciliationNewService';

// Extracted components
import MatchingPanelHeader from './MatchingPanelHeader';
import TransactionsPanel from './TransactionsPanel';
import JournalEntriesPanel from './JournalEntriesPanel';
import AutoMatchSuggestions from './AutoMatchSuggestions';

const TransactionMatchingPanel = ({
  selectedStatement,
  transactions,
  journalEntries,
  selectedTransactions,
  selectedJournalEntries,
  isLoadingTransactions,
  isLoadingJournalEntries,
  onTransactionToggle,
  onJournalEntryToggle,
  onReconcile,
  onFindMatches,
  canReconcile,
  selectionDifference,
  selectedTransactionTotal,
  selectedJournalEntryTotal,
  transactionTypeFilter,
  onTransactionTypeFilterChange,
  reconciliationFilter,
  onReconciliationFilterChange,
  executeUnreconciliation, // Add executeUnreconciliation prop
  onCreateEntry, // Add create entry function
  onBulkEditStart,
  onBulkCreateEntries,
  onEdit,
  onDelete,
}) => {
  const [transactionSearch, setTransactionSearch] = useState('');
  const [journalEntrySearch, setJournalEntrySearch] = useState('');
  const [autoMatches, setAutoMatches] = useState([]);
  const [isAutoMatching, setIsAutoMatching] = useState(false);

  // Sorting states
  const [transactionSortBy, setTransactionSortBy] = useState('amount'); // 'amount', 'date', 'description'
  const [transactionSortOrder, setTransactionSortOrder] = useState('desc'); // 'asc', 'desc'
  const [journalSortBy, setJournalSortBy] = useState('value'); // 'value', 'date', 'description'
  const [journalSortOrder, setJournalSortOrder] = useState('desc'); // 'asc', 'desc'

  // Helper function to toggle sort
  const toggleSort = useCallback(
    (type, currentSortBy, currentSortOrder, sortBy, setSortBy, setSortOrder) => {
      if (currentSortBy === sortBy) {
        // Same column, toggle order
        setSortOrder(currentSortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        // Different column, set new sort and default to desc for amounts, asc for others
        setSortBy(sortBy);
        setSortOrder(sortBy === 'amount' || sortBy === 'value' ? 'desc' : 'asc');
      }
    },
    []
  );

  // Filter and sort transactions
  const filteredTransactions = useMemo(() => {
    // console.log('[TransactionMatchingPanel] Filtering transactions:', {
    //   totalTransactions: transactions.length,
    //   showOnlyUnreconciled,
    //   transactionTypeFilter,
    //   sortBy: transactionSortBy,
    //   sortOrder: transactionSortOrder
    // });

    const filtered = transactions.filter((transaction) => {
      // Reconciliation status filter using reconciliationFilter
      if (reconciliationFilter === 'unreconciled' && isReconciled(transaction)) {
        return false;
      }
      if (reconciliationFilter === 'reconciled' && !isReconciled(transaction)) {
        return false;
      }
      // If reconciliationFilter === 'all', show both reconciled and unreconciled

      // Filter by transaction type using unified filter
      if (transactionTypeFilter === 'entrada' && transaction.amount <= 0) return false;
      if (transactionTypeFilter === 'saida' && transaction.amount >= 0) return false;

      if (transactionSearch) {
        const search = transactionSearch.toLowerCase();
        return (
          transaction.description?.toLowerCase().includes(search) ||
          transaction.amount?.toString().includes(search) ||
          transaction.checkNumber?.toLowerCase().includes(search)
        );
      }

      return true;
    });

    // Sort the filtered transactions
    const sorted = [...filtered].sort((a, b) => {
      let compareValue = 0;

      switch (transactionSortBy) {
        case 'amount':
          compareValue = Math.abs(a.amount) - Math.abs(b.amount);
          break;
        case 'date':
          compareValue = new Date(a.datePosted) - new Date(b.datePosted);
          break;
        case 'description':
          compareValue = (a.description || '').localeCompare(b.description || '');
          break;
        default:
          compareValue = 0;
      }

      return transactionSortOrder === 'desc' ? -compareValue : compareValue;
    });

    // console.log('[TransactionMatchingPanel] Filter result:', {
    //   originalTotal: transactions.length,
    //   reconciledInOriginal: reconciledCount,
    //   unreconciledInOriginal: unreconciledCount,
    //   filteredTotal: sorted.length,
    //   showOnlyUnreconciled,
    //   sortBy: transactionSortBy,
    //   sortOrder: transactionSortOrder,
    //   sampleTransaction: transactions[0] ? {
    //     isReconciled: transactions[0].isReconciled,
    //     amount: transactions[0].amount,
    //     description: transactions[0].description?.substring(0, 50)
    //   } : null
    // });

    return sorted;
  }, [
    transactions,
    transactionSearch,
    reconciliationFilter,
    transactionTypeFilter,
    transactionSortBy,
    transactionSortOrder,
  ]);

  // Filter and sort journal entries using the same unified filter
  const filteredJournalEntries = useMemo(() => {
    console.log('[TransactionMatchingPanel] Processing journal entries:', {
      totalJournalEntries: journalEntries.length,
      reconciliationFilter,
      transactionTypeFilter,
      journalEntrySearch,
    });

    const filtered = journalEntries.filter((entry) => {
      // Log first few entries for debugging
      if (journalEntries.indexOf(entry) < 3) {
        console.log(`[TransactionMatchingPanel] Sample entry ${journalEntries.indexOf(entry)}:`, {
          id: entry.id,
          description: entry.description?.substring(0, 30),
          isReconciled: isReconciled(entry),
          reconciliationData: entry.reconciliationData,
          isInput: entry.isInput,
          value: entry.value,
        });
      }

      // Reconciliation status filter using reconciliationFilter
      if (reconciliationFilter === 'unreconciled' && isReconciled(entry)) {
        if (journalEntries.indexOf(entry) < 3) {
          console.log(
            `[TransactionMatchingPanel] Filtering out reconciled entry ${journalEntries.indexOf(
              entry
            )}`
          );
        }
        return false;
      }
      if (reconciliationFilter === 'reconciled' && !isReconciled(entry)) {
        return false;
      }
      // If reconciliationFilter === 'all', show both reconciled and unreconciled

      // Filter by entry type using unified filter
      if (transactionTypeFilter === 'entrada' && !entry.isInput) return false;
      if (transactionTypeFilter === 'saida' && entry.isInput) return false;

      if (journalEntrySearch) {
        const search = journalEntrySearch.toLowerCase();
        return (
          entry.description?.toLowerCase().includes(search) ||
          entry.value?.toString().includes(search) ||
          entry.categoryName?.toLowerCase().includes(search)
        );
      }

      return true;
    });

    console.log('[TransactionMatchingPanel] Journal entries filter result:', {
      originalTotal: journalEntries.length,
      filteredTotal: filtered.length,
      reconciliationFilter,
      reconciledInOriginal: journalEntries.filter((entry) => isReconciled(entry)).length,
      unreconciledInOriginal: journalEntries.filter((entry) => !isReconciled(entry)).length,
    });

    // Sort the filtered journal entries
    const sorted = [...filtered].sort((a, b) => {
      let compareValue = 0;

      switch (journalSortBy) {
        case 'value':
          compareValue = Math.abs(a.valueFinal ?? a.value) - Math.abs(b.valueFinal ?? b.value);
          break;
        case 'date':
          compareValue = new Date(a.dateVencimento) - new Date(b.dateVencimento);
          break;
        case 'description':
          compareValue = (a.description || '').localeCompare(b.description || '');
          break;
        default:
          compareValue = 0;
      }

      return journalSortOrder === 'desc' ? -compareValue : compareValue;
    });

    const reconciledCount = journalEntries.filter((entry) => isReconciled(entry)).length;
    const unreconciledCount = journalEntries.filter((entry) => !isReconciled(entry)).length;

    console.log('[TransactionMatchingPanel] Journal entries filter result:', {
      originalTotal: journalEntries.length,
      reconciledInOriginal: reconciledCount,
      unreconciledInOriginal: unreconciledCount,
      filteredTotal: sorted.length,
      reconciliationFilter,
      transactionTypeFilter,
      sortBy: journalSortBy,
      sortOrder: journalSortOrder,
    });

    return sorted;
  }, [
    journalEntries,
    journalEntrySearch,
    reconciliationFilter,
    transactionTypeFilter,
    journalSortBy,
    journalSortOrder,
  ]);

  const handleAutoMatch = useCallback(async () => {
    // Get only unreconciled transactions
    const unreconciledTransactions = filteredTransactions.filter(
      (transaction) => !isReconciled(transaction)
    );

    if (unreconciledTransactions.length === 0) {
      toastNotification('info', 'Todas as transações já estão conciliadas!', {
        title: 'Nenhuma ação necessária',
      });
      return;
    }

    setIsAutoMatching(true);
    try {
      // Group transactions by exact amount
      const amountGroups = {};
      unreconciledTransactions.forEach((transaction) => {
        const amount = Math.abs(transaction.amount);
        if (!amountGroups[amount]) {
          amountGroups[amount] = [];
        }
        amountGroups[amount].push(transaction);
      });

      // Find groups with multiple transactions (potential bulk reconciliation)
      const bulkGroups = Object.entries(amountGroups)
        .filter(([amount, transactions]) => transactions.length > 1)
        .map(([amount, transactions]) => ({
          amount: parseFloat(amount),
          transactions,
          count: transactions.length,
        }))
        .sort((a, b) => b.count - a.count); // Sort by group size (largest first)

      if (bulkGroups.length === 0) {
        toastNotification(
          'info',
          'Não há transações com valores iguais para conciliar automaticamente.',
          {
            title: 'Sugestão manual necessária',
          }
        );
        return;
      }

      // Create auto-matches for bulk reconciliation
      const autoMatches = bulkGroups.map((group) => ({
        amount: group.amount,
        transactionCount: group.count,
        transactions: group.transactions,
        type: 'bulk_group',
        description: `${group.count} transações de R$ ${group.amount.toFixed(2)}`,
      }));

      setAutoMatches(autoMatches);

      toastNotification(
        'success',
        `Encontramos ${bulkGroups.length} grupo${
          bulkGroups.length > 1 ? 's' : ''
        } de transações com valores iguais! Selecione os grupos abaixo para conciliar automaticamente.`,
        {
          title: 'Sugestões encontradas!',
        }
      );
    } catch (error) {
      console.error('Auto-match error:', error);
      toastNotification(
        'error',
        'Não foi possível buscar sugestões automáticas. Tente novamente.',
        {
          title: 'Erro na busca automática',
        }
      );
    } finally {
      setIsAutoMatching(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTransactions, isReconciled]);

  const buildBulkPairsByValue = useCallback(() => {
    // Greedily match each transaction to the closest-value journal entry still available,
    // breaking ties by same month/year. Sorting both lists independently by value (the old
    // approach) breaks down whenever two or more selected items share a near-identical value
    // (e.g. several boletos around R$ 1.111,xx): each list gets ordered on its own, so ties can
    // land in a different relative order on each side and cross-pair items from different
    // months, which then fails the "mesmo mes e ano" check even though the user visually paired
    // matching rows correctly.
    const availableEntries = [...selectedJournalEntries];
    const pairs = [];

    selectedTransactions.forEach((txn) => {
      const txnAmount = Math.abs(parseFloat(txn.amount) || 0);
      const txnDate = new Date(txn.datePosted);

      let bestIndex = -1;
      let bestScore = Infinity;

      availableEntries.forEach((entry, idx) => {
        const entryValue = Math.abs(parseFloat(entry.value) || 0);
        const entryDate = new Date(entry.dateVencimento || entry.dateCompetencia);
        const valueDiff = Math.abs(txnAmount - entryValue);
        const sameMonth =
          !Number.isNaN(entryDate.getTime()) &&
          !Number.isNaN(txnDate.getTime()) &&
          entryDate.getMonth() === txnDate.getMonth() &&
          entryDate.getFullYear() === txnDate.getFullYear();
        const score = valueDiff + (sameMonth ? 0 : 1000000);

        if (score < bestScore) {
          bestScore = score;
          bestIndex = idx;
        }
      });

      if (bestIndex === -1) return;

      const [entry] = availableEntries.splice(bestIndex, 1);
      const pairId = `pair_${txn.transactionHash || pairs.length}_${Date.now()}`;
      pairs.push({ id: pairId, transaction: txn, journalEntry: entry });
    });

    return pairs;
  }, [selectedTransactions, selectedJournalEntries]);

  const validateBulkPairs = useCallback((pairs) => {
    for (const pair of pairs) {
      const txn = pair.transaction;
      const entry = pair.journalEntry;

      const entryDate = new Date(entry.dateVencimento || entry.dateCompetencia);
      const txnDate = new Date(txn.datePosted);

      if (
        !entryDate ||
        !txnDate ||
        Number.isNaN(entryDate.getTime()) ||
        Number.isNaN(txnDate.getTime())
      ) {
        return 'Datas invalidas nos itens selecionados.';
      }

      if (
        entryDate.getMonth() !== txnDate.getMonth() ||
        entryDate.getFullYear() !== txnDate.getFullYear()
      ) {
        return 'Conciliação 1:1 exige que os pares estejam no mesmo mes e ano.';
      }

      const txnAmount = Math.abs(parseFloat(txn.amount) || 0);
      const entryValue = Math.abs(parseFloat(entry.value) || 0);
      const difference = Math.abs(txnAmount - entryValue);
      const maxTotal = Math.max(txnAmount, entryValue);
      const valueTolerance = Math.max(maxTotal * 0.1, 10.0);

      if (difference > valueTolerance) {
        return 'Conciliação 1:1 bloqueada: ha pares com valores fora da tolerancia.';
      }
    }

    return null;
  }, []);

  const handleBulkReconcileByValue = useCallback(() => {
    if (!selectedTransactions.length || !selectedJournalEntries.length) {
      toastNotification('warning', 'Selecione extratos e lançamentos para conciliar.');
      return;
    }

    if (selectedTransactions.length !== selectedJournalEntries.length) {
      toastNotification(
        'warning',
        'Conciliação 1:1 requer a mesma quantidade de extratos e lançamentos selecionados.'
      );
      return;
    }

    const hasReconciled =
      selectedTransactions.some((txn) => isReconciled(txn)) ||
      selectedJournalEntries.some((entry) => isReconciled(entry));

    if (hasReconciled) {
      toastNotification('warning', 'Há itens já conciliados na seleção. Remova-os para continuar.');
      return;
    }

    const pairs = buildBulkPairsByValue();
    const validationError = validateBulkPairs(pairs);
    if (validationError) {
      toastNotification('warning', validationError);
      return;
    }

    if (onBulkEditStart) {
      onBulkEditStart(pairs);
    }
  }, [
    selectedTransactions,
    selectedJournalEntries,
    buildBulkPairsByValue,
    validateBulkPairs,
    onBulkEditStart,
  ]);

  const isTransactionSelected = useCallback(
    (transaction) => {
      return selectedTransactions.some((t) => t.transactionHash === transaction.transactionHash);
    },
    [selectedTransactions]
  );

  const isJournalEntrySelected = useCallback(
    (entry) => {
      return selectedJournalEntries.some((e) => e.id === entry.id);
    },
    [selectedJournalEntries]
  );

  // Functions to select/deselect all items
  const handleSelectAllTransactions = useCallback(
    (checked) => {
      if (checked) {
        // Select all filtered transactions that are not already selected
        const newSelections = filteredTransactions.filter(
          (transaction) => !isTransactionSelected(transaction)
        );
        newSelections.forEach((transaction) => onTransactionToggle(transaction));
      } else {
        // Deselect all currently selected transactions
        selectedTransactions.forEach((transaction) => onTransactionToggle(transaction));
      }
    },
    [filteredTransactions, selectedTransactions, isTransactionSelected, onTransactionToggle]
  );

  const handleSelectAllJournalEntries = useCallback(
    (checked) => {
      if (checked) {
        // Select all filtered journal entries that are not already selected
        const newSelections = filteredJournalEntries.filter(
          (entry) => !isJournalEntrySelected(entry)
        );
        newSelections.forEach((entry) => onJournalEntryToggle(entry));
      } else {
        // Deselect all currently selected journal entries
        selectedJournalEntries.forEach((entry) => onJournalEntryToggle(entry));
      }
    },
    [filteredJournalEntries, selectedJournalEntries, isJournalEntrySelected, onJournalEntryToggle]
  );

  // Check if all filtered items are selected
  const areAllTransactionsSelected = useMemo(() => {
    return filteredTransactions.length > 0 && filteredTransactions.every(isTransactionSelected);
  }, [filteredTransactions, isTransactionSelected]);

  const areAllJournalEntriesSelected = useMemo(() => {
    return (
      filteredJournalEntries.length > 0 && filteredJournalEntries.every(isJournalEntrySelected)
    );
  }, [filteredJournalEntries, isJournalEntrySelected]);

  const handleUnreconcile = useCallback(
    async (reconciliationId, item) => {
      try {
        console.log(
          `[TransactionMatchingPanel] Attempting to unreconcile ${reconciliationId}`,
          item
        );

        // Use the executeUnreconciliation function from the hook if available
        if (executeUnreconciliation) {
          await executeUnreconciliation(reconciliationId, item);
        } else {
          // Fallback to direct API call if the hook function is not available
          const companyId = localStorage.getItem('companyId');
          const userId = localStorage.getItem('userId') || 'unknown-user';

          if (!companyId) {
            toastNotification('Erro: Empresa não identificada', 'error');
            return;
          }

          const result = await removeReconciliation(reconciliationId, companyId, userId);

          if (result.success) {
            toastNotification('Conciliação removida com sucesso', 'success');
            // Note: mutate() calls in the hook will automatically refresh the data
          } else {
            toastNotification(result.message || 'Erro ao remover conciliação', 'error');
          }
        }
      } catch (error) {
        console.error('[TransactionMatchingPanel] Error unreconciling:', error);
        // Error is already handled by executeUnreconciliation, no need to show additional notification
      }
    },
    [executeUnreconciliation]
  );

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0,
      }}
    >
      <MatchingPanelHeader
        selectedStatement={selectedStatement}
        transactionsCount={transactions.length}
        journalEntriesCount={journalEntries.length}
        transactionTypeFilter={transactionTypeFilter}
        onTransactionTypeFilterChange={onTransactionTypeFilterChange}
        reconciliationFilter={reconciliationFilter}
        onReconciliationFilterChange={onReconciliationFilterChange}
        onAutoMatch={handleAutoMatch}
        isAutoMatching={isAutoMatching}
        showAutoMatch={false}
        canReconcile={canReconcile}
        onReconcile={onReconcile}
        canBulkCreate={
          selectedTransactions.length > 1 &&
          selectedJournalEntries.length === 0 &&
          !selectedTransactions.some((t) => t.isReconciled === true)
        }
        onBulkCreateEntries={() => onBulkCreateEntries && onBulkCreateEntries(selectedTransactions)}
        selectedTransactionsCount={selectedTransactions.length}
        canBulkReconcile={
          selectedTransactions.length > 1 &&
          selectedTransactions.length === selectedJournalEntries.length &&
          !selectedTransactions.some((t) => isReconciled(t)) &&
          !selectedJournalEntries.some((e) => isReconciled(e))
        }
        onBulkReconcile={handleBulkReconcileByValue}
      />

      {/* Main Content Area */}
      <Box
        sx={{
          flex: 1,
          overflow: 'hidden',
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflow: 'hidden',
            height: '100%',
            minHeight: 0,
            display: 'flex',
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <JournalEntriesPanel
            filteredJournalEntries={filteredJournalEntries}
            selectedJournalEntries={selectedJournalEntries}
            transactionTypeFilter={transactionTypeFilter}
            journalSortBy={journalSortBy}
            journalSortOrder={journalSortOrder}
            onJournalEntryToggle={onJournalEntryToggle}
            onSelectAllJournalEntries={handleSelectAllJournalEntries}
            areAllJournalEntriesSelected={areAllJournalEntriesSelected}
            onSortChange={(sortBy) =>
              toggleSort(
                'journal',
                journalSortBy,
                journalSortOrder,
                sortBy,
                setJournalSortBy,
                setJournalSortOrder
              )
            }
            isLoadingJournalEntries={isLoadingJournalEntries}
            journalEntrySearch={journalEntrySearch}
            onJournalEntrySearchChange={setJournalEntrySearch}
            reconciliationFilter={reconciliationFilter}
            onUnreconcile={handleUnreconcile}
            onEdit={onEdit}
            onDelete={onDelete}
          />
          <TransactionsPanel
            filteredTransactions={filteredTransactions}
            selectedTransactions={selectedTransactions}
            selectedJournalEntries={selectedJournalEntries}
            transactionTypeFilter={transactionTypeFilter}
            transactionSortBy={transactionSortBy}
            transactionSortOrder={transactionSortOrder}
            onTransactionToggle={onTransactionToggle}
            onSelectAllTransactions={handleSelectAllTransactions}
            areAllTransactionsSelected={areAllTransactionsSelected}
            onSortChange={(sortBy) =>
              toggleSort(
                'transaction',
                transactionSortBy,
                transactionSortOrder,
                sortBy,
                setTransactionSortBy,
                setTransactionSortOrder
              )
            }
            isLoadingTransactions={isLoadingTransactions}
            transactionSearch={transactionSearch}
            onTransactionSearchChange={setTransactionSearch}
            reconciliationFilter={reconciliationFilter}
            onUnreconcile={handleUnreconcile}
            onCreateEntry={onCreateEntry}
            onBulkReconcile={handleBulkReconcileByValue}
            onBulkCreateEntries={onBulkCreateEntries}
          />
        </Box>
      </Box>

      <AutoMatchSuggestions
        autoMatches={autoMatches}
        onSelectMatch={(match) => {
          if (match.type === 'bulk_group') {
            // For bulk groups, select the transactions for manual 1:1 reconciliation
            match.transactions.forEach((transaction) => onTransactionToggle(transaction));
          } else {
            // For individual matches, select the transaction and journal entry
            onTransactionToggle(match.transaction);
            onJournalEntryToggle(match.journalEntry);
          }
        }}
      />

    </Box>
  );
};

export default TransactionMatchingPanel;
